import { useEffect, useRef, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Camera, MapPin, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function RoofPhotoCapture({ leadId }) {
  const [photos, setPhotos] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const fileRef = useRef(null);
  const [pendingGps, setPendingGps] = useState(null);

  const load = () => {
    if (!leadId) return;
    api.get(`/documents?owner_type=lead&owner_id=${leadId}`)
      .then((r) => setPhotos(r.data.filter((d) => d.doc_type === "Roof Photo")));
  };
  useEffect(() => { load(); }, [leadId]);

  const grabGpsThenOpenCamera = () => {
    if (!leadId) { toast.error("Select a lead first"); return; }
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setCapturing(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPendingGps({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        if (fileRef.current) fileRef.current.click();
      },
      (err) => {
        setCapturing(false);
        toast.error(`Location denied: ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) { setCapturing(false); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("owner_type", "lead");
    fd.append("owner_id", leadId);
    fd.append("doc_type", "Roof Photo");
    if (pendingGps) {
      fd.append("gps_lat", pendingGps.lat);
      fd.append("gps_lng", pendingGps.lng);
      fd.append("gps_accuracy", pendingGps.accuracy);
    }
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Roof photo saved with GPS");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setCapturing(false);
      setPendingGps(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this roof photo?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div data-testid="roof-photo-capture">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow">Roof Photos (GPS-tagged)</div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFile}
          className="hidden"
          data-testid="roof-photo-input"
        />
        <Button
          onClick={grabGpsThenOpenCamera}
          disabled={capturing || !leadId}
          className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-9"
          data-testid="capture-roof-photo"
        >
          <Camera className="w-3 h-3 mr-2" /> {capturing ? "Getting GPS…" : "Capture Photo"}
        </Button>
      </div>

      {photos.length === 0 ? (
        <div className="text-xs text-[#9CA3AF] italic py-4 text-center border border-dashed border-[#E5E7EB]">
          No roof photos yet. Tap "Capture Photo" — uses your phone's rear camera and stamps the location.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((p) => (
            <PhotoTile key={p.id} doc={p} onRemove={() => remove(p.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoTile({ doc, onRemove }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    api.get(`/documents/${doc.id}/download`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [doc.id]);

  const hasGps = doc.gps_lat != null && doc.gps_lng != null;
  const mapsUrl = hasGps ? `https://www.google.com/maps?q=${doc.gps_lat},${doc.gps_lng}` : null;

  return (
    <div className="border border-[#E5E7EB]" data-testid={`roof-photo-${doc.id}`}>
      <div className="aspect-square bg-[#F3F4F6] flex items-center justify-center overflow-hidden">
        {src ? <img src={src} alt={doc.filename} className="w-full h-full object-cover" /> :
          <Camera className="w-6 h-6 text-[#9CA3AF]" />}
      </div>
      <div className="p-2 text-[10px] space-y-1">
        <div className="text-[#4B5563]">{fmtDate(doc.created_at)}</div>
        {hasGps ? (
          <a href={mapsUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#002FA7] hover:underline font-semibold">
            <MapPin className="w-3 h-3" />
            {doc.gps_lat.toFixed(5)}, {doc.gps_lng.toFixed(5)}
          </a>
        ) : (
          <span className="text-[#9CA3AF]">No GPS</span>
        )}
        <button onClick={onRemove} className="text-[#EF4444] hover:underline flex items-center gap-1">
          <Trash2 className="w-3 h-3" /> Remove
        </button>
      </div>
    </div>
  );
}
